'use client';

import { useId, useState, type ReactNode } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Checkbox,
  Modal,
  OutcomeList,
  Skeleton,
  StatusIcon,
  toast,
  type OutcomeItem,
  type StatusIconVariant,
} from '@mokoboko/components';
import { ERROR_STATUS_ICON_CLASS } from '@/lib/error-ramp';
import { Bell, Layers, ListOrdered, LogIn, Timer, Zap } from 'lucide-react';

import { TermsAcceptance, TermsLoadError } from '@/components';
import { LoginDialog } from '@/components/dialogs/login';
import { redirectsSignedInVisitor, useAuth } from '@/contexts/auth';
import { classDatePath } from '@/helpers/offering';
import {
  CREDIT_EXPIRY_ACK_LABEL,
  creditExpiresBeforeClassText,
  creditExpiryRefusalOf,
  type CreditExpiryRefusal,
  findActiveEntryForSlot,
  getModeRow,
  joinCheckboxErrorOf,
  holdSourceLabelOf,
  noCreditCopyOf,
  type NoCreditAction,
  type NoCreditCopy,
} from '@/helpers/waitlist';
import { OCCURRENCE_UNAVAILABLE_CODES } from '@/lib/constants';
import {
  getErrorCode,
  getErrorDetails,
  getErrorMessage,
} from '@/lib/error-utils';
import { isLoginRequired } from '@/lib/http-adapter';
import { AnalyticsEvent, track } from '@/lib/analytics/tracker';
import {
  classWithInstructorText,
  formatClassDateTime,
  getUserTimezone,
  type TimeFormat,
} from '@/lib/utils';
import { joinWaitlist } from '@/services/waitlist';
import type {
  WaitlistEligibility,
  WaitlistEligibilityReason,
  WaitlistOccurrence,
} from '@/services/waitlist';
import useTenant, { type WaitlistConfig, useTimeFormat } from '@/state/tenant';
import useMyWaitlistEntries, {
  invalidateWaitlistRelated,
  useWaitlistEligibility,
  waitlistEntryQueryKey,
} from '@/state/waitlist';
import { useTermsAcceptance } from '@/hooks/use-terms-acceptance';

export type JoinWaitlistSlot = Omit<WaitlistOccurrence, 'end_time'>;

type JoinWaitlistClass = { class_id: string; name: string; slug: string };

interface JoinWaitlistModalProps {
  onClose: () => void;
  classData: JoinWaitlistClass;
  slot: JoinWaitlistSlot;
  instructorName?: string;
}

const modeIconOf = (config: WaitlistConfig): ReactNode => {
  if (config.mode === 'AUTO_BOOK') return <Bell />;
  if (config.mode === 'CONFIRM') return <Timer />;
  return <Zap />;
};

const modeRowOf = (
  config: WaitlistConfig,
  slot: JoinWaitlistSlot,
  timeFormat: TimeFormat
): OutcomeItem => ({
  icon: modeIconOf(config),
  text: getModeRow(config, {
    startTime: slot.start_time,
    timeFormat,
    timeZone: slot.timezone,
  }),
});

const buildLoggedOutSteps = (
  config: WaitlistConfig | null,
  slot: JoinWaitlistSlot,
  timeFormat: TimeFormat
): OutcomeItem[] => [
  { icon: <LogIn />, text: 'Log in to your account' },
  { icon: <Layers />, text: 'We hold 1 credit while you wait' },
  config
    ? modeRowOf(config, slot, timeFormat)
    : {
        icon: <Bell />,
        text: 'If a spot opens, you get it or your credit back',
      },
];

const buildEligibleRows = ({
  slot,
  timeFormat,
  config,
  positionIfJoined,
  creditAtRisk,
}: {
  slot: JoinWaitlistSlot;
  timeFormat: TimeFormat;
  config: WaitlistConfig;
  positionIfJoined: number | undefined;
  creditAtRisk: boolean;
}): OutcomeItem[] => [
  ...(positionIfJoined != null
    ? [{ icon: <ListOrdered />, text: `Number ${positionIfJoined} in line` }]
    : []),
  modeRowOf(config, slot, timeFormat),
  {
    icon: <Layers />,
    // The give-back promise is false for a credit that expires first; the
    // acknowledgement block below says what happens instead.
    text: `1 credit on hold${
      creditAtRisk ? ' while you wait' : ", returned if you don't get a spot"
    }`,
  },
];

const CreditExpiryAcknowledgement = ({
  text,
  checked,
  hasError,
  errorMessage,
  shake,
  onChange,
}: {
  text: string;
  checked: boolean;
  hasError: boolean;
  errorMessage?: string;
  shake: number;
  onChange: (checked: boolean) => void;
}) => (
  <div className="flex flex-col gap-4">
    <Checkbox
      checked={checked}
      onCheckedChange={(value) => onChange(value === true)}
      hasError={hasError}
      errorMessage={errorMessage}
      shake={shake}
      label={
        <span className="font-normal text-gray-700">
          {CREDIT_EXPIRY_ACK_LABEL}
        </span>
      }
    />
    <Alert variant="warning" icon={null}>
      <span className="font-semibold">Heads up:</span> {text}
    </Alert>
  </div>
);

type IneligibleReason = Exclude<WaitlistEligibilityReason, 'LOGIN_REQUIRED'>;

type HeaderCopy = {
  title: string;
  description: string;
  variant: StatusIconVariant;
  showIcon?: boolean;
};

// No-credit copy depends on the probe's credit options, so noCreditCopyOf
// owns it.
const INELIGIBLE_COPY: Record<
  Exclude<IneligibleReason, 'WAITLIST_NO_CREDIT'>,
  HeaderCopy
> = {
  ALREADY_WAITLISTED: {
    title: 'You are already on this waitlist',
    description: 'Your place in line is safe. You can check it any time.',
    variant: 'info',
  },
  ALREADY_BOOKED: {
    title: 'You already booked this class',
    description: 'No need to wait, you have a spot.',
    variant: 'success',
  },
  WAITLIST_FULL: {
    title: 'The waitlist is full',
    description:
      'This waitlist has no more places. Try another class or check back later.',
    variant: 'warning',
  },
  WAITLIST_CLOSED: {
    title: 'The waitlist is closed',
    description: 'The waitlist closed because the class starts soon.',
    variant: 'warning',
  },
  WAITLIST_DISABLED: {
    title: 'The waitlist is not available',
    description: 'This class does not have a waitlist.',
    variant: 'warning',
  },
  OCCURRENCE_NOT_FOUND: {
    title: 'This class time is no longer available',
    description:
      'The calendar may be out of date. Close this and pick a time again.',
    variant: 'warning',
  },
  CLASS_NOT_FULL: {
    title: 'A spot is available',
    description: 'No need to wait. You can book this class now.',
    variant: 'success',
  },
};

// The join answers with the probe's reasons, plus the occurrence checks only
// the join runs; each maps to the copy that already explains it.
const refusalReasonOf = (code: string | undefined): IneligibleReason | null => {
  if (!code) return null;
  if (OCCURRENCE_UNAVAILABLE_CODES.includes(code)) {
    return 'OCCURRENCE_NOT_FOUND';
  }
  return code === 'WAITLIST_NO_CREDIT' ||
    Object.keys(INELIGIBLE_COPY).includes(code)
    ? (code as IneligibleReason)
    : null;
};

const headerCopy = ({
  needsLogin,
  isError,
  reason,
  classLine,
  noCredit,
}: {
  needsLogin: boolean;
  isError: boolean;
  reason: WaitlistEligibilityReason | undefined;
  classLine: string;
  noCredit: NoCreditCopy;
}): HeaderCopy => {
  // Disabling a query keeps its last status, so a stale probe error would
  // otherwise paint the logged-out view red.
  if (needsLogin) {
    return {
      title: 'Join the waitlist',
      description:
        'The class is full. Log in and we can hold your place in line.',
      variant: 'info',
      showIcon: false,
    };
  }
  if (isError) {
    return {
      title: 'We could not check this class',
      description: 'Please try again in a moment.',
      variant: 'error',
    };
  }
  if (reason === 'WAITLIST_NO_CREDIT') {
    return {
      title: noCredit.title,
      description: noCredit.description,
      variant: 'warning',
    };
  }
  if (reason && reason !== 'LOGIN_REQUIRED') return INELIGIBLE_COPY[reason];
  return {
    title: 'Join the waitlist',
    description: classLine,
    variant: 'info',
    showIcon: false,
  };
};

const classLineOf = (
  className: string,
  instructorName: string | undefined,
  slot: JoinWaitlistSlot,
  timeFormat: TimeFormat
): string =>
  `${classWithInstructorText(className, instructorName)} · ${formatClassDateTime(slot.start_time, timeFormat, slot.timezone, 'short')}`;

type BodyState =
  | 'login'
  | 'error'
  | 'loading'
  | 'eligible'
  | 'no_credit'
  | 'already_waitlisted'
  | 'not_full'
  | 'closed';

const bodyStateOf = ({
  needsLogin,
  isError,
  isLoading,
  hasConfig,
  eligible,
  reason,
}: {
  needsLogin: boolean;
  isError: boolean;
  isLoading: boolean;
  hasConfig: boolean;
  eligible: boolean;
  reason: WaitlistEligibilityReason | undefined;
}): BodyState => {
  if (needsLogin) return 'login';
  // Ahead of the skeleton: `!config` never resolving must not bury a
  // failed probe under bars that animate forever.
  if (isError) return 'error';
  if (isLoading || !hasConfig) return 'loading';
  if (eligible) return 'eligible';
  if (reason === 'WAITLIST_NO_CREDIT') return 'no_credit';
  if (reason === 'ALREADY_WAITLISTED') return 'already_waitlisted';
  if (reason === 'CLASS_NOT_FULL') return 'not_full';
  return 'closed';
};

const ModalFooter = ({
  primary,
  closeDisabled,
}: {
  primary?: ReactNode;
  closeDisabled?: boolean;
}) => (
  <Modal.Footer>
    <Modal.Close asChild>
      <Button
        variant="secondary-gray"
        disabled={closeDisabled}
        className="ml-auto"
      >
        Go back
      </Button>
    </Modal.Close>
    {primary}
  </Modal.Footer>
);

const LoggedOutBody = ({
  config,
  slot,
  timeFormat,
  onLogin,
}: {
  config: WaitlistConfig | null;
  slot: JoinWaitlistSlot;
  timeFormat: TimeFormat;
  onLogin: () => void;
}) => (
  <>
    <Modal.Body>
      <OutcomeList items={buildLoggedOutSteps(config, slot, timeFormat)} />
    </Modal.Body>
    <ModalFooter
      primary={
        <Button variant="primary" onClick={onLogin}>
          Log in
        </Button>
      }
    />
  </>
);

const LoadingBody = () => (
  <Modal.Body className="pb-8">
    <div className="flex flex-col gap-3">
      <Skeleton text="body-sm" width="75%" />
      <Skeleton text="body-sm" width="66%" />
      <Skeleton text="body-sm" width="80%" />
    </div>
  </Modal.Body>
);

const EligibleBody = ({
  classData,
  slot,
  positionIfJoined,
  config,
  preview,
  isProcessing,
  onProcessingChange,
  onRefused,
  onLoginRequired,
  onPreviewStale,
  onClose,
}: {
  classData: JoinWaitlistClass;
  slot: JoinWaitlistSlot;
  positionIfJoined: number | undefined;
  config: WaitlistConfig;
  preview: WaitlistEligibility['hold_preview'];
  isProcessing: boolean;
  onProcessingChange: (isProcessing: boolean) => void;
  onRefused: (reason: IneligibleReason) => void;
  onLoginRequired: () => void;
  onPreviewStale: () => void;
  onClose: () => void;
}) => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const timeFormat = useTimeFormat();
  const checkboxErrorId = useId();
  // The lock-free preview can pick a different source than the join locks, so
  // a CREDIT_EXPIRES_BEFORE_CLASS refusal arms the warning on its own, with
  // the source and date the join locked.
  const [expiryRefusal, setExpiryRefusal] =
    useState<CreditExpiryRefusal | null>(null);
  const [ackChecked, setAckChecked] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [shake, setShake] = useState(0);
  const terms = useTermsAcceptance({
    type: 'CLASS',
    entityIds: [classData.class_id],
  });

  const sourceLabel = expiryRefusal
    ? expiryRefusal.sourceLabel
    : preview
      ? holdSourceLabelOf(preview)
      : null;
  const expiresAt = expiryRefusal
    ? expiryRefusal.expiresAt
    : (preview?.expires_at ?? null);
  const creditAtRisk = !!expiryRefusal || !!preview?.expires_before_cutoff;
  const termsMissing = terms.showCheckbox && !terms.checked;
  const ackMissing = creditAtRisk && !ackChecked;
  const checkboxError = showErrors
    ? joinCheckboxErrorOf({ termsMissing, ackMissing })
    : null;
  // Both unticked, the one message sits under the credit box, so the terms
  // box names it as its description too.
  const termsErrorId =
    checkboxError && termsMissing && ackMissing ? checkboxErrorId : undefined;

  const handleJoin = async () => {
    if (termsMissing || ackMissing) {
      setShowErrors(true);
      setShake((count) => count + 1);
      return;
    }
    onProcessingChange(true);
    try {
      const { entry } = await joinWaitlist({
        class_id: classData.class_id,
        instructor_schedule_id: slot.instructor_schedule_id,
        slot_id: slot.slot_id,
        override_slot_id: slot.override_slot_id,
        start_time: slot.start_time,
        customer_timezone: getUserTimezone(),
        ...(creditAtRisk ? { accept_credit_expiry: true } : {}),
        ...terms.buildPayload(),
      });
      track({
        event: AnalyticsEvent.WAITLIST_JOINED,
        properties: {
          class_id: classData.class_id,
          mode: entry.mode,
          position: entry.position,
        },
      });
      invalidateWaitlistRelated(queryClient);
      queryClient.setQueryData(
        waitlistEntryQueryKey(entry.waitlist_entry_id),
        entry
      );
      onClose();
      router.push(`/waitlist/${entry.waitlist_entry_id}`);
    } catch (error) {
      const code = getErrorCode(error);
      const refusal = refusalReasonOf(code);
      if (refusal === 'CLASS_NOT_FULL') {
        toast.info('A spot is available. You can book it now.');
        invalidateWaitlistRelated(queryClient);
        onClose();
      } else if (refusal) {
        onRefused(refusal);
      } else if (code === 'CREDIT_EXPIRES_BEFORE_CLASS') {
        setExpiryRefusal(creditExpiryRefusalOf(getErrorDetails(error)));
        setAckChecked(false);
        setShowErrors(false);
        onPreviewStale();
      } else if (isLoginRequired(error)) {
        onLoginRequired();
      } else if (terms.handleSubmitError(error)) {
        // The server's terms-changed message, not the unticked-box one an
        // earlier failed Join left switched on.
        setShowErrors(false);
      } else {
        toast.error(
          getErrorMessage(
            error,
            'Unable to join the waitlist, please try again later.'
          )
        );
      }
    } finally {
      onProcessingChange(false);
    }
  };

  return (
    <>
      <Modal.Body>
        <div className="flex flex-col gap-4">
          <OutcomeList
            items={buildEligibleRows({
              slot,
              timeFormat,
              config,
              positionIfJoined,
              creditAtRisk,
            })}
          />
          {!terms.isLoading &&
            (terms.isError ? (
              <TermsLoadError onRetry={terms.refetch} />
            ) : (
              <TermsAcceptance
                terms={terms}
                hasError={(showErrors && termsMissing) || !!terms.error}
                errorMessage={
                  (!ackMissing && checkboxError) || terms.error || undefined
                }
                shake={shake}
                consentAction="join the waitlist"
                describedBy={termsErrorId}
              />
            ))}
          {termsErrorId && (
            <span id={termsErrorId} hidden>
              {checkboxError}
            </span>
          )}
          {creditAtRisk && (
            <CreditExpiryAcknowledgement
              text={creditExpiresBeforeClassText(
                sourceLabel ?? 'credits',
                expiresAt
              )}
              checked={ackChecked}
              hasError={showErrors && ackMissing}
              errorMessage={checkboxError ?? undefined}
              shake={shake}
              onChange={setAckChecked}
            />
          )}
        </div>
      </Modal.Body>
      <ModalFooter
        closeDisabled={isProcessing}
        primary={
          <Button
            variant="primary"
            onClick={handleJoin}
            isProcessing={isProcessing}
            disabled={terms.isLoading || terms.isError}
          >
            Join waitlist
          </Button>
        }
      />
    </>
  );
};

const NoCreditFooter = ({ actions }: { actions: NoCreditAction[] }) => {
  const router = useRouter();
  if (!actions.length) return <DismissFooter />;
  return (
    <ModalFooter
      primary={actions.map((action, index) => (
        <Button
          key={action.href}
          variant={index === actions.length - 1 ? 'primary' : 'secondary-gray'}
          onClick={() => router.push(action.href)}
        >
          {action.label}
        </Button>
      ))}
    />
  );
};

const AlreadyWaitlistedFooter = ({ slot }: { slot: JoinWaitlistSlot }) => {
  const router = useRouter();
  // The card and calendar open this modal only when their cached list had no
  // place for the slot, so that list predates the one the probe found.
  const myEntries = useMyWaitlistEntries({ fresh: true });
  const existingEntry = findActiveEntryForSlot(myEntries.data, slot);
  return (
    <ModalFooter
      primary={
        <Button
          variant="primary"
          isProcessing={myEntries.isFetching && !existingEntry}
          disabled={!existingEntry}
          onClick={() =>
            existingEntry &&
            router.push(`/waitlist/${existingEntry.waitlist_entry_id}`)
          }
        >
          View waitlist
        </Button>
      }
    />
  );
};

// The class can be booked again, so the calendar behind the modal is stale.
const NotFullFooter = ({ onClose }: { onClose: () => void }) => {
  const queryClient = useQueryClient();
  return (
    <Modal.Footer>
      <Button
        variant="primary"
        className="ml-auto"
        onClick={() => {
          invalidateWaitlistRelated(queryClient);
          onClose();
        }}
      >
        Got it
      </Button>
    </Modal.Footer>
  );
};

const DismissFooter = () => (
  <Modal.Footer>
    <Modal.Close asChild>
      <Button variant="primary" className="ml-auto">
        Got it
      </Button>
    </Modal.Close>
  </Modal.Footer>
);

export const JoinWaitlistModal = ({
  onClose,
  classData,
  slot,
  instructorName,
}: JoinWaitlistModalProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();
  const { data: tenant } = useTenant();
  const config = tenant?.waitlist_config ?? null;
  const timeFormat = useTimeFormat();

  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  // Join can race a fill-up; the server's verdict replaces the cached one.
  const [reasonOverride, setReasonOverride] =
    useState<WaitlistEligibilityReason | null>(null);

  const eligibility = useWaitlistEligibility({
    class_id: classData.class_id,
    slot_id: slot.slot_id,
    override_slot_id: slot.override_slot_id,
    start_time: slot.start_time,
  });

  const reason = reasonOverride ?? eligibility.data?.reason;
  // A probe that still says LOGIN_REQUIRED after the silent refresh means the
  // session is gone, whatever the cached user says.
  const needsLogin = !isAuthenticated || reason === 'LOGIN_REQUIRED';
  const positionIfJoined =
    config?.mode === 'CLAIM' ? undefined : eligibility.data?.position_if_joined;
  const noCredit = noCreditCopyOf(
    eligibility.data?.credit_options,
    classData.class_id
  );
  const bodyState = bodyStateOf({
    needsLogin,
    isError: eligibility.isError,
    isLoading: eligibility.isLoading,
    hasConfig: !!config,
    eligible: !!eligibility.data?.eligible && !reasonOverride,
    reason,
  });

  const handleRefused = (refusal: IneligibleReason) => {
    setReasonOverride(refusal);
    invalidateWaitlistRelated(queryClient);
  };

  // A login on the home page sends her on to the dashboard, unmounting this
  // modal mid-join. The site's login-and-return path brings her back to this
  // class time instead.
  const handleLogin = () => {
    if (redirectsSignedInVisitor(pathname)) {
      onClose();
      router.push(
        `/?action=login&redirect=${encodeURIComponent(
          classDatePath(classData.slug, slot.start_time)
        )}`
      );
      return;
    }
    setIsLoginOpen(true);
  };

  const {
    title,
    description,
    variant,
    showIcon = true,
  } = headerCopy({
    needsLogin,
    isError: eligibility.isError,
    reason,
    classLine: classLineOf(classData.name, instructorName, slot, timeFormat),
    noCredit,
  });

  return (
    <>
      <Modal.Root
        open
        onOpenChange={(open) => {
          if (!open && !isProcessing) onClose();
        }}
      >
        <Modal.Content size="sm">
          <Modal.Header>
            {showIcon && (
              <div className="mb-3">
                <StatusIcon
                  variant={variant}
                  size="xl"
                  className={
                    variant === 'error' ? ERROR_STATUS_ICON_CLASS : undefined
                  }
                />
              </div>
            )}
            <Modal.Title>{title}</Modal.Title>
            <Modal.Description>{description}</Modal.Description>
          </Modal.Header>
          {bodyState === 'login' && (
            <LoggedOutBody
              config={config}
              slot={slot}
              timeFormat={timeFormat}
              onLogin={handleLogin}
            />
          )}
          {bodyState === 'error' && (
            <ModalFooter
              primary={
                <Button
                  variant="primary"
                  onClick={() => eligibility.refetch()}
                  isProcessing={eligibility.isFetching}
                >
                  Try again
                </Button>
              }
            />
          )}
          {bodyState === 'loading' && <LoadingBody />}
          {bodyState === 'eligible' && config && (
            <EligibleBody
              classData={classData}
              slot={slot}
              positionIfJoined={positionIfJoined}
              config={config}
              preview={eligibility.data?.hold_preview}
              isProcessing={isProcessing}
              onProcessingChange={setIsProcessing}
              onRefused={handleRefused}
              onLoginRequired={() => setIsLoginOpen(true)}
              onPreviewStale={() => eligibility.refetch()}
              onClose={onClose}
            />
          )}
          {bodyState === 'no_credit' && (
            <NoCreditFooter actions={noCredit.actions} />
          )}
          {bodyState === 'already_waitlisted' && (
            <AlreadyWaitlistedFooter slot={slot} />
          )}
          {bodyState === 'not_full' && <NotFullFooter onClose={onClose} />}
          {bodyState === 'closed' && <DismissFooter />}
        </Modal.Content>
      </Modal.Root>

      <LoginDialog
        open={isLoginOpen}
        onClose={() => setIsLoginOpen(false)}
        onConfirm={() => {
          setIsLoginOpen(false);
          // Signed in already means the probe said LOGIN_REQUIRED: same user,
          // same query key, so it has to be asked again by hand.
          if (isAuthenticated) void eligibility.refetch();
        }}
      />
    </>
  );
};
